import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { HeatMap, makeSteppedHeatMap, SequentialGreenRamp } from '../../../utils/colors';

// The shade a cell falls back to when it has no usable value to place on a ramp.
const SEQUENTIAL_MID_STOP = SequentialGreenRamp[Math.floor(SequentialGreenRamp.length / 2)];

// TYPES, INTERFACES, CONSTANTS
export interface IntermediateMetrics {
  spend: number;
  impressions: number;
  clicks: number;
  volume: number;
  revenue: number;
  roas: number;
  cpx: number;
}

export interface DisplayedMetrics {
  spend: number;
  cpc: number;
  ctr: number;
  ccr: number;
  volume: number;
  revenue: number;
  roas: number;
  cpx: number;
}

const DisplayedMetricsKeys: (keyof DisplayedMetrics)[] = [
  'spend',
  'cpc',
  'ctr',
  'ccr',
  'volume',
  'revenue',
  'roas',
  'cpx'
];

export const DISPLAYED_METRICS_LABELS: { key: keyof DisplayedMetrics; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'cpc', label: 'CPC' },
  { key: 'ctr', label: 'CTR' },
  { key: 'ccr', label: 'Clicks Conversion Rate' },
  { key: 'volume', label: 'KPI Volume' },
  { key: 'revenue', label: 'KPI Revenue' },
  { key: 'roas', label: 'ROAS' },
  { key: 'cpx', label: 'CPX' }
];

// whether higher or lower values get darker colors
const MIN_IS_BEST_METRICS = {
  spend: true,
  cpc: true,
  ctr: false,
  ccr: false,
  volume: false,
  revenue: false,
  roas: false,
  cpx: true
};

interface HeatMapData {
  max: number;
  min: number;
}

export type HeatMapRanges = {
  [key in keyof DisplayedMetrics]: HeatMapData;
};

// FORMATTERS
export const formatMetricValue = (
  value: number,
  formatter: (val: number, decimals?: number) => string,
  decimals?: number
): string => {
  if (value <= 0) {
    return '--';
  }
  return formatter(value, decimals);
};

const safeRatio = (numerator: number, denominator: number, multiplier = 1): number => {
  if (numerator >= 0 && denominator > 0) {
    return (numerator / denominator) * multiplier;
  }
  return -1;
};

export const displayedMetricsFormatters = (
  metric: keyof DisplayedMetrics
): ((value: number) => string) => {
  switch (metric) {
    case 'spend':
      return (value: number) => formatMetricValue(value, formatMoney, 0);
    case 'cpc':
      return (value: number) => formatMetricValue(value, formatNumber, 2);
    case 'ctr':
      return (value: number) => `${formatMetricValue(value, formatNumber, 2)}%`;
    case 'ccr':
      return (value: number) => `${formatMetricValue(value, formatNumber, 2)}%`;
    case 'volume':
      return (value: number) => formatMetricValue(value, formatNumber, 0);
    case 'revenue':
      return (value: number) => formatMetricValue(value, formatMoney, 0);
    case 'roas':
      return (value: number) => formatMetricValue(value, formatNumber, 2);
    case 'cpx':
      return (value: number) => formatMetricValue(value, formatMoney, 0);
    default:
      return (value: number) => formatMetricValue(value, formatNumber, 0);
  }
};

// HEATMAP UTILS
export const calculateDisplayedMetricsMap = (
  intermediateMetrics: IntermediateMetrics
): DisplayedMetrics => {
  const { spend, impressions, clicks, volume, revenue, roas, cpx } = intermediateMetrics;

  const cpc = safeRatio(spend, clicks);
  const ctr = safeRatio(clicks, impressions, 100);
  const ccr = safeRatio(volume, clicks, 100);

  return { spend, cpc, ctr, ccr, volume, revenue, roas, cpx };
};

export const calculateHeatMapRanges = (
  displayedMetricsMap: Record<string, DisplayedMetrics>
): HeatMapRanges => {
  const ranges: Partial<HeatMapRanges> = {};

  // initialize ranges for all metrics
  DisplayedMetricsKeys.forEach(metric => {
    ranges[metric] = {
      max: -Infinity,
      min: Infinity
    };
  });

  // calculate ranges for all metrics — ranges[metric] is guaranteed by the
  // initialization loop above, so we assert non-null after the lookup.
  Object.values(displayedMetricsMap).forEach(displayedMetrics => {
    DisplayedMetricsKeys.forEach(metric => {
      const value = displayedMetrics[metric];
      const range = ranges[metric]!;
      range.min = Math.min(range.min, value);
      range.max = Math.max(range.max, value);
    });
  });

  return ranges as HeatMapRanges;
};

export const generateHeatMaps = (
  heatMapRanges: HeatMapRanges
): Record<keyof DisplayedMetrics, HeatMap> => {
  const heatMaps: Partial<Record<keyof DisplayedMetrics, HeatMap>> = {};

  DisplayedMetricsKeys.forEach(metric => {
    const range = heatMapRanges[metric];
    const minIsBest = MIN_IS_BEST_METRICS[metric];

    // No `midpoint`: this is a magnitude scale, so the ramp should span the domain in one arm
    // rather than being split in half around its center.
    const heatMap = makeSteppedHeatMap({
      min: range.min,
      max: range.max,
      stops: minIsBest ? [...SequentialGreenRamp].reverse() : SequentialGreenRamp
    });

    heatMaps[metric] = heatMap;
  });

  return heatMaps as Record<keyof DisplayedMetrics, HeatMap>;
};

export const getHeatMapColor = (
  value: number,
  metric: keyof DisplayedMetrics,
  heatMaps: Record<keyof DisplayedMetrics, HeatMap>
): { backgroundColor: string } => {
  const heatMap = heatMaps[metric];
  if (!heatMap || value < 0) {
    return { backgroundColor: SEQUENTIAL_MID_STOP }; // default to medium
  }
  return { backgroundColor: heatMap(value) };
};
